import { Stack } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { entry } from '@/james-playground/aa-family-claim/claim/registry';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';
import {
  CardShell,
  EntryField,
  EntryToggle,
  hasProblem,
  useDraft,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  storedAs,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/** Kept out of the reply: the conversation is a transcript anyone may scroll. */
const PRIVATE = new Set(['ni_number']);

/**
 * Details that have to be exact, typed together: an NI number, phone
 * numbers, the GP's address, dates abroad. Any of them may stay blank; a blank
 * one stays open for later. Continue needs one filled and none mistyped.
 */
const DetailsCard = ({ ask, facts, voice: v, onSubmit, onSkip }: CardProps) => {
  const { draft, set, visible } = useDraft(ask, facts);
  const ids = ask.entries.filter(visible);
  const filled = ids.filter((id) => str(draft[id]).trim());
  const ready =
    filled.length > 0 && !ids.some((id) => hasProblem(id, draft[id]));

  const submit = () => {
    const values = Object.fromEntries(
      filled.map((id) => [id, storedAs(entry(id).answerType, str(draft[id]))]),
    );
    const reply = filled
      .map((id) => {
        const type = entry(id).answerType;
        if (PRIVATE.has(type)) return `${labelOf(id, v)} added`;
        if (type === 'yes_no') return `${labelOf(id, v)}: ${values[id]}`;
        return values[id].replace(/\n/g, ', ');
      })
      .join(' · ');
    onSubmit(values, reply);
  };

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Stack spacing={2}>
        {ids.map((id) =>
          entry(id).answerType === 'yes_no' ||
          entry(id).answerType === 'single_choice' ? (
            <EntryToggle
              key={id}
              id={id}
              v={v}
              value={str(draft[id])}
              onChange={(value) => set(id, value)}
            />
          ) : (
            <EntryField
              key={id}
              id={id}
              v={v}
              value={str(draft[id])}
              onChange={(value) => set(id, value)}
            />
          ),
        )}
      </Stack>
    </CardShell>
  );
};

DetailsCard.displayName = 'DetailsCard';
export default DetailsCard;
